import { useEffect, useState } from 'react';
import { Plus, Edit, Trash2, X, Save, Warehouse, ArrowRight, Calendar, Loader2 } from 'lucide-react';
import { warehouseService } from '../services/warehouseService';
import { driverService } from '../services/driverService';
import { vehicleService } from '../services/vehicleService';
import { authService } from '../services/authService';

interface Transfer {
  id: string;
  transferNumber: string;
  sourceWarehouse: string;
  targetWarehouse: string;
  sourceWarehouseId: string;
  targetWarehouseId: string;
  productType: string;
  quantity: string;
  unit: 'Palet' | 'Koli' | 'Adet' | 'Ton';
  transferDate: string;
  estimatedArrival: string;
  vehicle: string;
  driver: string;
  status: 'Planlandı' | 'Yüklendi' | 'Yolda' | 'Teslim Edildi' | 'İptal';
  notes: string;
}

type TransferStatus = Transfer['status'];

const defaultFormData: Partial<Transfer> = {
  status: 'Planlandı',
  unit: 'Palet',
  transferDate: new Date().toISOString().split('T')[0],
};

const normalizeTransferStatus = (status?: string): TransferStatus => {
  switch (status) {
    case 'planned':
    case 'Planlandı':
      return 'Planlandı';
    case 'loaded':
    case 'Yüklendi':
      return 'Yüklendi';
    case 'in_transit':
    case 'in-progress':
    case 'Yolda':
      return 'Yolda';
    case 'delivered':
    case 'completed':
    case 'Teslim Edildi':
      return 'Teslim Edildi';
    case 'cancelled':
    case 'İptal':
      return 'İptal';
    default:
      return 'Planlandı';
  }
};

const mapTransfer = (item: any): Transfer => ({
  id: String(item.id),
  transferNumber: item.transfer_no || item.transferNumber || '',
  sourceWarehouse: item.from_warehouse_name || '',
  targetWarehouse: item.to_warehouse_name || '',
  sourceWarehouseId: String(item.from_warehouse_id || ''),
  targetWarehouseId: String(item.to_warehouse_id || ''),
  productType: item.product_name || item.productType || '',
  quantity: String(item.quantity || ''),
  unit: item.unit || 'Palet',
  transferDate: (item.transfer_date || '').split('T')[0],
  estimatedArrival: (item.estimated_arrival || '').split('T')[0],
  vehicle: item.vehicle_plate || '',
  driver: item.driver_name || '',
  status: normalizeTransferStatus(item.status),
  notes: item.notes || '',
});

export function WarehouseTransfer() {
  const [data, setData] = useState<Transfer[]>([]);
  const [warehouses, setWarehouses] = useState<any[]>([]);
  const [drivers, setDrivers] = useState<any[]>([]);
  const [vehicles, setVehicles] = useState<any[]>([]);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<Transfer | null>(null);
  const [formData, setFormData] = useState<Partial<Transfer>>(defaultFormData);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const [transfersRes, warehousesRes, driversRes, vehiclesRes] = await Promise.all([
        warehouseService.getTransfers(),
        warehouseService.getWarehouses(),
        driverService.getDrivers(),
        vehicleService.getVehicles(),
      ]);

      setData(transfersRes.success && transfersRes.data ? transfersRes.data.map(mapTransfer) : []);
      setWarehouses(warehousesRes.success && warehousesRes.data ? warehousesRes.data : []);
      setDrivers(driversRes.success && driversRes.data ? driversRes.data : []);
      setVehicles(vehiclesRes.success && vehiclesRes.data ? vehiclesRes.data : []);
    } catch (err: any) {
      console.error('Error loading warehouse transfers:', err);
      setError(err.message || 'Veriler yüklenirken hata oluştu');
    } finally {
      setIsLoading(false);
    }
  };

  const columns = [
    { key: 'transferNumber', label: 'Transfer No' },
    { key: 'sourceWarehouse', label: 'Kaynak Depo' },
    { key: 'targetWarehouse', label: 'Hedef Depo' },
    { key: 'productType', label: 'Ürün' },
    { key: 'quantity', label: 'Miktar' },
    { key: 'transferDate', label: 'Transfer Tarihi' },
    { key: 'status', label: 'Durum' },
  ];

  const openForm = (item?: Transfer) => {
    if (item) {
      setEditingItem(item);
      setFormData(item);
    } else {
      setEditingItem(null);
      setFormData(defaultFormData);
    }
    setIsFormOpen(true);
  };

  const closeForm = () => {
    setIsFormOpen(false);
    setEditingItem(null);
    setFormData(defaultFormData);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      const payload = {
        transferNo: editingItem?.transferNumber,
        fromWarehouseId: formData.sourceWarehouseId,
        toWarehouseId: formData.targetWarehouseId,
        productName: formData.productType,
        quantity: Number(formData.quantity || 0),
        unit: formData.unit,
        transferDate: formData.transferDate,
        estimatedArrival: formData.estimatedArrival || null,
        vehiclePlate: formData.vehicle || '',
        driverName: formData.driver || '',
        status: formData.status || 'Planlandı',
        notes: formData.notes || '',
        createdBy: authService.getStoredUser()?.id,
      };

      const response = editingItem
        ? await warehouseService.updateTransfer(editingItem.id, payload)
        : await warehouseService.createTransfer(payload);

      if (!response.success) {
        throw new Error(response.message || 'Transfer kaydı kaydedilemedi');
      }

      await loadData();
      closeForm();
    } catch (err: any) {
      console.error('Error saving transfer:', err);
      alert(err.message || 'Kayıt sırasında hata oluştu');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Bu transfer kaydını silmek istediğinizden emin misiniz?')) return;

    try {
      const response = await warehouseService.deleteTransfer(id);
      if (!response.success) {
        throw new Error(response.message || 'Transfer kaydı silinemedi');
      }
      await loadData();
    } catch (err: any) {
      console.error('Error deleting transfer:', err);
      alert(err.message || 'Silme sırasında hata oluştu');
    }
  };

  const renderCell = (item: Transfer, key: string) => {
    if (key === 'status') {
      const colors = {
        'Planlandı': 'bg-gray-100 text-gray-800',
        'Yüklendi': 'bg-blue-100 text-blue-800',
        'Yolda': 'bg-yellow-100 text-yellow-800',
        'Teslim Edildi': 'bg-green-100 text-green-800',
        'İptal': 'bg-red-100 text-red-800',
      };
      return <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[item.status]}`}>{item.status}</span>;
    }
    if (key === 'quantity') return `${item.quantity} ${item.unit}`;
    if (key === 'transferDate' && item.transferDate) return new Date(item.transferDate).toLocaleDateString('tr-TR');
    if (key === 'sourceWarehouse' || key === 'targetWarehouse') return <div className="max-w-[200px] truncate" title={item[key as keyof Transfer] as string}>{item[key as keyof Transfer]}</div>;
    return item[key as keyof Transfer] || '-';
  };

  const renderActions = (item: Transfer) => (
    <div className="flex gap-2">
      <button onClick={() => openForm(item)} className="p-1 text-blue-600 hover:bg-blue-50 rounded"><Edit className="w-4 h-4" /></button>
      <button onClick={() => handleDelete(item.id)} className="p-1 text-red-600 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4" /></button>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Depolar Arası Transfer</h1>
          <p className="text-gray-500 mt-1">Depo transfer işlemlerini planlayın ve takip edin</p>
        </div>
        <button onClick={() => openForm()} className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700">
          <Plus className="w-5 h-5" />
          Yeni Transfer
        </button>
      </div>

      {isLoading && (
        <div className="bg-white rounded-lg border border-gray-200 py-16 flex justify-center">
          <div className="flex flex-col items-center gap-3"><Loader2 className="w-8 h-8 text-blue-600 animate-spin" /><p className="text-gray-500">Transfer kayıtları yükleniyor...</p></div>
        </div>
      )}

      {error && !isLoading && (
        <div className="bg-white rounded-lg border border-red-200 py-12 text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button onClick={loadData} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Tekrar Dene</button>
        </div>
      )}

      {!isLoading && !error && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="bg-white rounded-lg shadow p-4"><div className="text-sm text-gray-500">Toplam Transfer</div><div className="text-2xl font-bold text-gray-900 mt-1">{data.length}</div></div>
            <div className="bg-white rounded-lg shadow p-4"><div className="text-sm text-gray-500">Planlandı</div><div className="text-2xl font-bold text-gray-600 mt-1">{data.filter((item) => item.status === 'Planlandı').length}</div></div>
            <div className="bg-white rounded-lg shadow p-4"><div className="text-sm text-gray-500">Yolda</div><div className="text-2xl font-bold text-yellow-600 mt-1">{data.filter((item) => item.status === 'Yolda').length}</div></div>
            <div className="bg-white rounded-lg shadow p-4"><div className="text-sm text-gray-500">Teslim Edildi</div><div className="text-2xl font-bold text-green-600 mt-1">{data.filter((item) => item.status === 'Teslim Edildi').length}</div></div>
            <div className="bg-white rounded-lg shadow p-4"><div className="text-sm text-gray-500">Bu Hafta</div><div className="text-2xl font-bold text-blue-600 mt-1">{data.filter((item) => { const transferDate = new Date(item.transferDate); const now = new Date(); const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000); return transferDate >= weekAgo && transferDate <= now; }).length}</div></div>
          </div>

          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    {columns.map((column) => <th key={column.key} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{column.label}</th>)}
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">İşlemler</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {data.map((item) => (
                    <tr key={item.id} className="hover:bg-gray-50">
                      {columns.map((column) => <td key={column.key} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{renderCell(item, column.key)}</td>)}
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{renderActions(item)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {isFormOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white">
              <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2"><Warehouse className="w-6 h-6" />{editingItem ? 'Transfer Düzenle' : 'Yeni Transfer Planla'}</h2>
              <button onClick={closeForm} className="text-gray-400 hover:text-gray-600"><X className="w-6 h-6" /></button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2"><ArrowRight className="w-5 h-5" />Transfer Bilgileri</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Kaynak Depo *</label>
                    <select required value={formData.sourceWarehouseId || ''} onChange={(e) => setFormData({ ...formData, sourceWarehouseId: e.target.value, sourceWarehouse: warehouses.find((warehouse) => String(warehouse.id) === e.target.value)?.name || '' })} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500">
                      <option value="">Seçiniz</option>
                      {warehouses.map((warehouse) => <option key={warehouse.id} value={String(warehouse.id)}>{warehouse.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Hedef Depo *</label>
                    <select required value={formData.targetWarehouseId || ''} onChange={(e) => setFormData({ ...formData, targetWarehouseId: e.target.value, targetWarehouse: warehouses.find((warehouse) => String(warehouse.id) === e.target.value)?.name || '' })} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500">
                      <option value="">Seçiniz</option>
                      {warehouses.filter((warehouse) => String(warehouse.id) !== formData.sourceWarehouseId).map((warehouse) => <option key={warehouse.id} value={String(warehouse.id)}>{warehouse.name}</option>)}
                    </select>
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Ürün Tipi *</label>
                    <input type="text" required value={formData.productType || ''} onChange={(e) => setFormData({ ...formData, productType: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Ürün adı" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Miktar *</label>
                    <input type="number" required value={formData.quantity || ''} onChange={(e) => setFormData({ ...formData, quantity: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Birim *</label>
                    <select required value={formData.unit || 'Palet'} onChange={(e) => setFormData({ ...formData, unit: e.target.value as Transfer['unit'] })} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500">
                      <option value="Palet">Palet</option><option value="Koli">Koli</option><option value="Adet">Adet</option><option value="Ton">Ton</option>
                    </select>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2"><Calendar className="w-5 h-5" />Planlama</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Transfer Tarihi *</label>
                    <input type="date" required value={formData.transferDate || ''} onChange={(e) => setFormData({ ...formData, transferDate: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tahmini Varış</label>
                    <input type="date" value={formData.estimatedArrival || ''} onChange={(e) => setFormData({ ...formData, estimatedArrival: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Araç Plakası</label>
                    <select value={formData.vehicle || ''} onChange={(e) => setFormData({ ...formData, vehicle: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500">
                      <option value="">Seçiniz</option>
                      {vehicles.map((vehicle) => {
                        const plate = vehicle.plate_number || vehicle.cekici_plaka || vehicle.plate || '';
                        return <option key={vehicle.id} value={plate}>{plate}</option>;
                      })}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Sürücü</label>
                    <select value={formData.driver || ''} onChange={(e) => setFormData({ ...formData, driver: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500">
                      <option value="">Seçiniz</option>
                      {drivers.map((driver) => {
                        const name = driver.full_name || driver.name || '';
                        return <option key={driver.id} value={name}>{name}</option>;
                      })}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Durum *</label>
                    <select required value={formData.status || 'Planlandı'} onChange={(e) => setFormData({ ...formData, status: e.target.value as Transfer['status'] })} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500">
                      <option value="Planlandı">Planlandı</option><option value="Yüklendi">Yüklendi</option><option value="Yolda">Yolda</option><option value="Teslim Edildi">Teslim Edildi</option><option value="İptal">İptal</option>
                    </select>
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Notlar</label>
                    <textarea value={formData.notes || ''} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" rows={3} placeholder="Özel notlar, talimatlar vb." />
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button type="button" onClick={closeForm} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">İptal</button>
                <button type="submit" disabled={isSaving} className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-60">
                  {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  {editingItem ? 'Güncelle' : 'Kaydet'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
